import _ from "lodash";
import {
  SimulationAssessmentReport,
  SimulationParameterEvaluation,
  SimulationSubParameterEvaluation,
} from "src/types/coaching/learners";

const EXCEL_SHEET_MAX_LEN = 31;
const ILLEGAL_SHEET_CHARS = /[\[\]\/\\:*?]+/g;

export interface SimulationOverallExportRow {
  parameter: string;
  score: number;
}

export interface SimulationSubparameterDetailRow {
  subParameter: string;
  score: number;
  positives: string;
  suggestions: string;
  missedOpportunities: string;
}

export const SIMULATION_OVERALL_SCHEMA = [
  {
    column: "Parameter",
    type: String,
    value: (row: SimulationOverallExportRow) => row.parameter,
  },
  {
    column: "Score",
    type: Number,
    value: (row: SimulationOverallExportRow) => row.score,
  },
];

export const SIMULATION_SUBPARAMETER_DETAIL_SCHEMA = [
  {
    column: "Sub-parameter",
    type: String,
    value: (row: SimulationSubparameterDetailRow) => row.subParameter,
  },
  {
    column: "Score",
    type: Number,
    value: (row: SimulationSubparameterDetailRow) => row.score,
  },
  {
    column: "Positives",
    type: String,
    wrap: true,
    value: (row: SimulationSubparameterDetailRow) => row.positives,
  },
  {
    column: "Suggestions",
    type: String,
    wrap: true,
    value: (row: SimulationSubparameterDetailRow) => row.suggestions,
  },
  {
    column: "Missed Opportunities",
    type: String,
    wrap: true,
    value: (row: SimulationSubparameterDetailRow) => row.missedOpportunities,
  },
];

export type SimulationLearnerReportSheetRows =
  | SimulationOverallExportRow[]
  | SimulationSubparameterDetailRow[];

export interface SimulationLearnerReportWorkbook {
  /** Index 0 = Overall; then one sheet per top-level parameter in API order */
  data: SimulationLearnerReportSheetRows[];
  schemas: (typeof SIMULATION_OVERALL_SCHEMA | typeof SIMULATION_SUBPARAMETER_DETAIL_SCHEMA)[];
  sheets: string[];
}

function roundScore(score: unknown): number {
  return typeof score === "number" && !Number.isNaN(score)
    ? parseFloat(score.toFixed(2))
    : 0;
}

/** Excel sheet name: non-empty, <=31 chars, no []/\:*? */
function allocateUniqueSheetName(rawName: string, index: number, used: Set<string>): string {
  let base = String(rawName ?? "")
    .replace(ILLEGAL_SHEET_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, EXCEL_SHEET_MAX_LEN);

  if (!base) {
    base = `Parameter_${index + 1}`.slice(0, EXCEL_SHEET_MAX_LEN);
  }

  let candidate = base.slice(0, EXCEL_SHEET_MAX_LEN);
  let n = 2;
  while (used.has(candidate)) {
    const suffix = `_${n}`;
    candidate = `${base.slice(0, EXCEL_SHEET_MAX_LEN - suffix.length)}${suffix}`;
    n += 1;
  }

  used.add(candidate);
  return candidate;
}

function buildDetailRows(
  top: SimulationParameterEvaluation,
): SimulationSubparameterDetailRow[] {
  const subs = top.parameters ?? [];
  const rows: SimulationSubparameterDetailRow[] = [];

  for (const sub of subs) {
    const name = sub.name?.trim();
    if (!name) {
      continue;
    }

    const missed = _.filter(sub.missedOpportunities ?? [], (m) => String(m).trim());
    const missedText = missed.length
    ? missed.map((m) => String(m).trim()).join("\n\n")
    : "";
    
    rows.push({
      subParameter: name,
      score: roundScore(sub.score),
      positives: sub.positives != null ? String(sub.positives) : "",
      suggestions: sub.suggestions != null ? String(sub.suggestions) : "",
      missedOpportunities: missedText,
    });
  }

  return rows;
}

/**
 * Builds multi-sheet workbook data: Sheet "Overall" (top-level Parameter + Score),
 * then one sheet per top-level parameter with sub-parameter detail rows (headers only if no subs).
 */
export function buildSimulationLearnerReportWorkbook(
  report: SimulationAssessmentReport | null,
): SimulationLearnerReportWorkbook | null {
  const parameters = report?.parameters;
  if (!parameters?.length) {
    return null;
  }

  const usedSheetNames = new Set<string>(["Overall"]);

  const overallRows: SimulationOverallExportRow[] = parameters.map((p) => ({
    parameter: p.name?.trim() ? p.name : "",
    score: roundScore(p.score),
  }));

  const data: SimulationLearnerReportSheetRows[] = [overallRows];
  const schemas: SimulationLearnerReportWorkbook["schemas"] = [SIMULATION_OVERALL_SCHEMA];
  const sheets: string[] = ["Overall"];

  parameters.forEach((p, i) => {
    const sheetTitle = allocateUniqueSheetName(p.name ?? `Parameter_${i + 1}`, i, usedSheetNames);
    sheets.push(sheetTitle);
    data.push(buildDetailRows(p));
    schemas.push(SIMULATION_SUBPARAMETER_DETAIL_SCHEMA);
  });

  return { data, schemas, sheets };
}


export const SIMULATION_LEARNER_REPORT_SCHEMA = SIMULATION_OVERALL_SCHEMA;

export function buildSimulationLearnerReportExportRows(
  report: SimulationAssessmentReport | null,
): SimulationOverallExportRow[] {
  const wb = buildSimulationLearnerReportWorkbook(report);
  if (!wb?.data[0]) {
    return [];
  }
  return wb.data[0] as SimulationOverallExportRow[];
}
